import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAnalytics } from '../context/AnalyticsContext';
import { ChevronDown, Share2, Check } from 'lucide-react';
import './Landing.css';

interface RoleCardProps {
  id: string;
  title: string;
  badge: string;
  badgeStyle: React.CSSProperties;
  children: React.ReactNode;
}

const RoleCard: React.FC<RoleCardProps> = ({ id, title, badge, badgeStyle, children }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const url = `${window.location.origin}/careers#${id}`;
    if (navigator.share) {
      await navigator.share({ title, url });
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div id={id} style={{ borderBottom: '1px solid #e5e7eb' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', padding: '1.5rem 0' }}>
        <button
          onClick={() => setOpen(o => !o)}
          style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left', flex: 1 }}
          aria-expanded={open}
        >
          <h3 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>{title}</h3>
          <span style={{ padding: '0.25rem 0.75rem', borderRadius: '999px', fontSize: '0.875rem', fontWeight: 500, whiteSpace: 'nowrap', ...badgeStyle }}>{badge}</span>
          <ChevronDown
            size={20}
            style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s', flexShrink: 0, color: '#6b7280' }}
          />
        </button>
        <button
          onClick={handleShare}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'none', border: '1px solid #d1d5db', borderRadius: '0.5rem', padding: '0.4rem 0.85rem', cursor: 'pointer', fontSize: '0.875rem', color: '#374151', flexShrink: 0 }}
        >
          {copied ? <Check size={15} /> : <Share2 size={15} />}
          {copied ? 'Copied!' : 'Share'}
        </button>
      </div>

      {open && (
        <div style={{ paddingBottom: '2rem' }}>
          {children}
        </div>
      )}
    </div>
  );
};

const Careers: React.FC = () => {
  const { trackPageView } = useAnalytics();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    trackPageView('/careers', 'Careers');
  }, [trackPageView]);

  return (
    <div className="app">
      <Header />

      <main className="main-content">
        {/* Hero Section */}
        <section className="hero">
          <div className="hero-content">
            <h1 className="hero-title">Join Our Team</h1>
            <p className="hero-subtitle">
              Help us make activity planning easier for families everywhere.
            </p>
          </div>
        </section>

        {/* Intro Section */}
        <section className="about-content">
          <div className="content-container">
            <div className="about-section">
              <h2 className="section-title">Why Work at Aika</h2>
              <div className="section-content">
                <div className="section-text">
                  <p>
                    Aika is an early-stage startup on a mission to take the stress out of activity planning for busy families. We're a small, focused team that moves fast, cares deeply about the families we serve, and believes that good technology should make everyday life simpler.
                  </p>
                  <p>
                    We're based in Boulder, CO and work in a hybrid environment. We value autonomy, clear communication, and a bias toward action. If you're excited about building something meaningful from the ground up, we'd love to hear from you.
                  </p>
                </div>
                <div className="section-image">
                  <img src="/images/science.png" alt="Team working together" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="values-section">
          <div className="content-container">
            <h2 className="section-title">What We Value in Our Team</h2>
            <div className="values-grid">
              <div className="value-card">
                <div className="value-icon">🚀</div>
                <h3 className="value-title">Ownership</h3>
                <p className="value-description">
                  We trust people to take initiative, make decisions, and see things through without waiting to be asked.
                </p>
              </div>
              <div className="value-card">
                <div className="value-icon">💬</div>
                <h3 className="value-title">Clear Communication</h3>
                <p className="value-description">
                  We write and speak clearly, share context generously, and give direct, respectful feedback.
                </p>
              </div>
              <div className="value-card">
                <div className="value-icon">❤️</div>
                <h3 className="value-title">Empathy for Families</h3>
                <p className="value-description">
                  Everything we build starts with understanding the real challenges parents and kids face every day.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Open Roles Section */}
        <section className="about-content">
          <div className="content-container">
            <h2 className="section-title">Open Positions</h2>

            <RoleCard
              id="marketing-intern"
              title="Summer Marketing Intern"
              badge="6 weeks · Starts June 1"
              badgeStyle={{ background: '#f0fdf4', color: '#16a34a' }}
            >
              <p>
                Aika is looking for a summer marketing intern for a 6-week project starting June 1st. You'll help us experiment with growth and distribution as we expand into the broader family scheduling space.
              </p>
              <p><strong>The internship will focus on:</strong></p>
              <ul style={{ paddingLeft: '1.25rem', lineHeight: '1.9' }}>
                <li>Developing a social media and marketing content strategy</li>
                <li>Experimenting with different growth and distribution channels</li>
                <li>Learning and testing modern marketing tools</li>
                <li>Creating and analyzing content across platforms</li>
                <li>Understanding how an early-stage startup thinks about positioning, messaging, and customer acquisition</li>
              </ul>
              <p>You'll have a small marketing budget to experiment with and a chance to see what startup marketing actually looks like behind the scenes.</p>
              <p><strong>This could be a great fit for:</strong></p>
              <ul style={{ paddingLeft: '1.25rem', lineHeight: '1.9' }}>
                <li>A college student interested in marketing, communications, or product growth</li>
                <li>Someone curious about entrepreneurship and early-stage companies</li>
                <li>A self-starter who enjoys trying things, learning quickly, and being creative</li>
              </ul>
              <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>
                Unpaid · ~15–20 hrs/week · Boulder/Denver (hybrid) or remote in DC, Philadelphia, or Chicago
              </p>
              <p>
                Interested? Email <a href="mailto:careers@aika.camp" style={{ color: 'inherit', textDecoration: 'underline' }}>careers@aika.camp</a> with a little about yourself and why this sounds interesting to you.
              </p>
            </RoleCard>

            <RoleCard
              id="product-intern"
              title="Summer Product Intern"
              badge="6 weeks · Starts June 1"
              badgeStyle={{ background: '#f0fdf4', color: '#16a34a' }}
            >
              <p>
                Aika is looking for a summer product intern for a 6-week project starting June 1st. You'll work closely with an experienced product leader and get real exposure to the product development process — from research and synthesis to interface design and iteration.
              </p>
              <p><strong>The internship will focus on:</strong></p>
              <ul style={{ paddingLeft: '1.25rem', lineHeight: '1.9' }}>
                <li>Conducting market and user research</li>
                <li>Identifying patterns, pain points, and opportunities from that research</li>
                <li>Learning how product decisions are actually made in early-stage startups</li>
                <li>Working under direct guidance to design and iterate on user interfaces</li>
                <li>Exploring how modern AI tools can accelerate product design and product thinking</li>
              </ul>
              <p><strong>This could be a great fit for:</strong></p>
              <ul style={{ paddingLeft: '1.25rem', lineHeight: '1.9' }}>
                <li>A college student interested in product management, UX/UI design, or human-centered design</li>
                <li>Someone curious about entrepreneurship and early-stage companies</li>
                <li>A self-starter who enjoys solving problems, learning quickly, and thinking creatively</li>
              </ul>
              <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>
                Unpaid · ~15–20 hrs/week · Boulder/Denver (hybrid) or remote in DC, Philadelphia, or Chicago
              </p>
              <p>
                Interested? Email <a href="mailto:careers@aika.camp" style={{ color: 'inherit', textDecoration: 'underline' }}>careers@aika.camp</a> with a little about yourself and why this sounds interesting to you.
              </p>
            </RoleCard>

            <RoleCard
              id="future-marketing"
              title="Future Interest — Marketing & Growth"
              badge="Future Opportunity"
              badgeStyle={{ background: '#eff6ff', color: '#2563eb' }}
            >
              <p>
                We're not actively hiring in marketing right now, but we're always interested in meeting people who are passionate about brand building, growth, and reaching families in meaningful ways. If you've been following what we're doing and want to be part of it when the time is right, we'd love to hear from you.
              </p>
              <p>
                We're also open to conversations with potential co-founders who bring a marketing or growth background. If you're excited about the problem we're solving and want to help shape the company from the ground up, that's a conversation worth having.
              </p>
              <p><strong>This could be a great fit for:</strong></p>
              <ul style={{ paddingLeft: '1.25rem', lineHeight: '1.9' }}>
                <li>An experienced marketer or growth specialist interested in joining an early-stage startup</li>
                <li>Someone with a background in brand, content, or community who wants to build something from scratch</li>
                <li>A potential co-founder with marketing or growth expertise looking for the right mission to invest in</li>
              </ul>
              <p>
                Reach out at <a href="mailto:careers@aika.camp" style={{ color: 'inherit', textDecoration: 'underline' }}>careers@aika.camp</a> and tell us a bit about yourself and what excites you about Aika.
              </p>
            </RoleCard>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Careers;
